/**
 * The add box's check of a selection before it is added: what the page shows, worked out
 * apart from the page so that it can be tested (EST-169, slice 3). Nothing here decides
 * what is a double: that is the server's answer (functions/mailshots/addCheck.js).
 */
import { classOf, swapFor, settle, RELEASABLE, LISTED } from '../models/addChoices';

const n = (v) => Number(v || 0).toLocaleString('en-GB');
const plural = (count, one, many) => (Number(count) === 1 ? one : many);
const homes = (count) => `${n(count)} ${plural(count, 'home', 'homes')}`;

/** The classes a person decides, in the order shown: do not contact first (no choice), then doubles. */
export const CLASSES = [
  { key: 'do-not-contact', title: 'Do not contact', words: 'Each is, or reads as, a home marked do not contact. Not added. Nothing here releases one.', release: false },
  { key: 'may-be-do-not-contact', title: 'May be do not contact', words: 'Each cannot be told from a home marked do not contact in its postcode, or shares a number with one. Not added. Nothing here releases one.', release: false },
  { key: 'in-mailshot:same', title: 'Already in this mailshot', words: 'The rule reads each as a home that is already in this mailshot. The one already there is not touched.', release: true },
  { key: 'in-mailshot:cannot-tell', title: 'May already be in this mailshot', words: 'The rule cannot tell each from a home that is already in this mailshot (a flat number on one side only, for example).', release: true },
  { key: 'in-selection:same', title: 'Twice in this selection', words: 'The rule reads each as another home in this selection. One of each is kept and goes in; these are the others.', release: true },
  { key: 'in-selection:cannot-tell', title: 'May be twice in this selection', words: 'The rule cannot tell each from another home in this selection. One of each is kept and goes in; these are the others.', release: true },
];

/**
 * Does an answer account for EVERY home that was asked of, once each, and nothing else?
 * An answer that does not is a failed check, never "no doubles": a home that is in no part
 * of the answer would be neither added nor shown as left out. Answers what is wrong, in
 * words, or null.
 */
export function unaccounted(answer, askedIds) {
  const a = answer; const c = a && a.check;
  if (!a || !c || !c.counts || !Array.isArray(c.clear) || !Array.isArray(c.listed) || !Array.isArray(c.alreadyIn)) return "the check's answer could not be read";
  if (a.missing !== undefined && !Array.isArray(a.missing)) return "the check's answer could not be read";
  if (c.listed.some((l) => !l || !LISTED[l.why] || !Array.isArray(l.against))) return 'the check listed a home for a reason this page does not know';
  if (c.counts.listed !== c.listed.length || c.counts.clear !== c.clear.length) return "the check's counts are not the lists it sent";
  const said = [...c.clear, ...c.alreadyIn, ...c.listed.map((l) => l.id), ...(a.missing || [])].map(String);
  const asked = [...new Set((askedIds || []).map(String))];
  if (new Set(said).size !== said.length) return 'the check named a home twice';
  if (said.length !== asked.length || asked.some((id) => !said.includes(id))) return 'the check did not account for every home it was asked about';
  // The homes it names as already in a sent mailshot (an older function names none: that is not a fault). A list that
  // names a home it was not asked about, names one twice, names no mailshot for one, names a mailshot it did not say
  // was sent, or is not as long as its own count, is a failed check: homes would be left out, or let in, on a list
  // that does not add up.
  if (a.alreadySent !== undefined) {
    if (!Array.isArray(a.alreadySent)) return "the check's answer could not be read";
    const sentMailshots = new Set((a.elsewhere || []).filter((m) => m && m.sent).map((m) => String(m.id)));
    const named = a.alreadySent.map((h) => String(h && h.id));
    if (new Set(named).size !== named.length) return 'the check named a home twice among those already sent';
    if (named.some((id) => !asked.includes(id))) return 'the check named, as already sent, a home it was not asked about';
    if (a.alreadySent.some((h) => !h || !Array.isArray(h.in) || h.in.length === 0 || h.in.some((m) => !sentMailshots.has(String(m))))) return 'the check named a home as already sent without a sent mailshot that holds it';
    if (Number.isInteger(a.sentElsewhere) && a.sentElsewhere !== a.alreadySent.length) return "the check's count of homes already sent is not the list it sent";
    // a mailshot named as not having stamped a home must be one of the sent mailshots that holds it
    if (a.alreadySent.some((h) => h.notStamped !== undefined && (!Array.isArray(h.notStamped) || h.notStamped.length === 0 || h.notStamped.some((m) => !h.in.map(String).includes(String(m)))))) return 'the check named a mailshot as not having stamped a home that it does not hold';
    // and each sent mailshot's own count is the number of homes named for it
    for (const m of (a.elsewhere || []).filter((x) => x && x.sent)) {
      const namedForIt = a.alreadySent.filter((h) => h.in.map(String).includes(String(m.id))).length;
      if (Number.isInteger(m.count) && m.count !== namedForIt) return "the check's list of homes already sent does not match its count for a mailshot";
    }
  }
  return null;
}

/**
 * HOMES ALREADY IN A MAILSHOT THAT HAS BEEN SENT (Lee's rule of 8 Oct 2026; built 9 Oct, before his first mailshot
 * with an import in it). The check says WHICH of the homes asked of are on the list of another mailshot that has
 * been sent. The box leaves them out unless a person unticks it.
 * ⚠️ BY RECORD, and by the mailshots' own lists. The same HOME sent before under another listing id (re-listed since)
 * is a different record and is NOT among them: nothing compares addresses across mailshots, and the box says so.
 * `notStamped`: of those mailshots, the ones that did NOT stamp the home as sent when they were marked as mailed,
 * because it was marked do not contact then (empty for most homes). See `NOT_STAMPED_WORDS`.
 * @returns {null | Array<{ id: string, mailshots: string[], notStamped: string[] }>} null when the answer does not say
 *   which (a function from before this: the page then says the count, as it did, and offers no box)
 */
export function sentHomes(answer) {
  const a = answer || {};
  if (!Array.isArray(a.alreadySent)) return null;
  const names = new Map((a.elsewhere || []).filter((m) => m && m.sent).map((m) => [String(m.id), m.name || String(m.id)]));
  const named = (ids) => (Array.isArray(ids) ? ids : []).map((m) => names.get(String(m)) || String(m));
  return a.alreadySent.map((h) => ({ id: String(h.id), mailshots: named(h.in), notStamped: named(h.notStamped) }));
}

/**
 * Said beside a home in "Show them" that a sent mailshot did NOT stamp. ⚠️ "Not stamped" is not "no letter went":
 * the mark is read when the mailshot is MARKED as mailed, not when its file was exported. A home marked after the
 * export and before the marking got a letter and is not stamped. So the home stays among those left out, and a
 * person is told what is and is not known.
 */
export const NOT_STAMPED_WORDS = (mailshots) => `NOT stamped as sent by ${mailshots.join(', ')}: it was marked do not contact when ${mailshots.length === 1 ? 'that mailshot was' : 'those mailshots were'} marked as mailed. Whether it was mailed depends on whether it was marked before the file was exported.`;

/** Said under the box, always: what "already sent" does and does not cover. */
export const SENT_BY_RECORD = 'This goes by record: a home on the list of a mailshot that has been sent. The same home sent before under another Rightmove listing is a different record, and is not caught here.';

/**
 * The box for homes already sent, and the homes it is about: those that WOULD be added (after the doubles and do not
 * contact are settled) and are on a sent mailshot's list. ONE SET: the number on the box, the "Show them" list and
 * the figure in the message after the add are all this.
 * @returns {null | { homes, count, label, untickedWords }} null when the answer does not say which homes
 */
export function sentChoice(answer, choices, leaveOut = true) {
  const all = sentHomes(answer);
  if (all === null) return null;
  const going = new Set(settle(answer && answer.check, choices).add.map(String));
  const these = all.filter((h) => going.has(h.id));
  const count = these.length;
  return {
    homes: these,
    count,
    label: `Leave out ${homes(count)} already in a mailshot that has been sent`,
    untickedWords: !leaveOut && count ? `${homes(count)} already in a mailshot that has been sent WILL be added.` : null,
  };
}

/** The ids of the homes a check listed as matching do not contact: they stay out, whatever is ticked afterwards. */
export const doNotContactIds = (answer) => ((answer && answer.check && answer.check.listed) || []).filter((l) => l.why === 'do-not-contact' || l.why === 'may-be-do-not-contact').map((l) => String(l.id));

/** A stable text for what a check was asked: the mailshot and the ids, in order. A change makes the check out of date. */
export const checkKey = (mailshotId, ids) => `${mailshotId || ''}|${(ids || []).join(',')}`;

/** The listed homes of an answer, by class, in the order of CLASSES; classes with none are left out. */
export function byClass(check) {
  const listed = (check && check.listed) || [];
  return CLASSES.map((c) => ({ ...c, entries: listed.filter((l) => classOf(l) === c.key) })).filter((c) => c.entries.length > 0);
}

/** The choices a person has for one listed home. "Leave it out" is first and is the default. */
export function choicesFor(check, entry) {
  if (!entry || entry.releasable !== true || !RELEASABLE.includes(entry.why)) return [];
  const out = [{ value: 'leave', label: 'Leave it out' }, { value: 'add', label: entry.why === 'in-mailshot' ? 'Add it as well' : 'Add both' }];
  if (swapFor(check, entry) !== null) out.push({ value: 'instead', label: 'Add this one instead of the other' });
  return out;
}

/** The choices with every home of a class set one way: 'add', or anything else for "leave it out". */
export function chooseClass(check, choices, classKey, value) {
  const next = { ...(choices || {}) };
  for (const l of (check && check.listed) || []) {
    if (classOf(l) !== classKey || l.releasable !== true) continue;
    if (value === 'add') next[l.id] = 'add'; else delete next[l.id];
  }
  return next;
}

/** One line of what the check found, counts first. */
export function summaryOf(answer) {
  const c = (answer && answer.check && answer.check.counts) || {};
  const parts = [`${homes(c.asked)} checked`, `${n(c.clear)} with nothing to say`];
  if (c.alreadyIn) parts.push(`${n(c.alreadyIn)} already in this mailshot`);
  if (c.inUnsearchedGroups) parts.push(`${n(c.inUnsearchedGroups)} in ${n(c.unsearchedGroups)} ${plural(c.unsearchedGroups, 'group', 'groups')} too large to work out, ALL being added: look at ${plural(c.unsearchedGroups, 'it', 'them')}`);
  const doubles = (c.inMailshot || 0) + (c.inSelection || 0);
  if (doubles) parts.push(`${n(doubles)} that read as a double (the rule says the same home for ${n(c.ruleSaysSame)}, and cannot tell for ${n(c.ruleCannotTell)})`);
  const dnc = (c.doNotContact || 0) + (c.mayBeDoNotContact || 0);
  if (dnc) parts.push(`${n(dnc)} that match do not contact`);
  return `${parts.join('; ')}.`;
}

/** The lines beneath the summary: things said, not decided. */
export function notesOf(answer) {
  const a = answer || {}; const c = (a.check && a.check.counts) || {}; const out = [];
  if ((a.missing || []).length) out.push(`${homes(a.missing.length)} named ${plural(a.missing.length, 'no longer exists and is', 'no longer exist and are')} left out.`);
  if (c.noPostcode) out.push(`${homes(c.noPostcode)} ${plural(c.noPostcode, 'has', 'have')} no postcode and could not be compared with anything.`);
  for (const m of a.elsewhere || []) out.push(`${homes(m.count)} ${plural(m.count, 'is', 'are')} also in "${m.name}"${m.sent ? ', which has been sent' : ''}.`);
  if (a.read && a.read.inMailshotNotFound) out.push(`${homes(a.read.inMailshotNotFound)} on this mailshot's list ${plural(a.read.inMailshotNotFound, 'has', 'have')} no record, and could not be compared with.`);
  return out;
}

/**
 * What goes to the add and what is left out, for the button and the message.
 * `leaveOutSent`: the box for homes already in a sent mailshot. Ticked, those homes are taken out of what is sent
 * to the add; they are counted in `leftOut`, and apart in `sentLeftOut`.
 */
export function settled(answer, choices, { leaveOutSent = false } = {}) {
  const s = settle(answer && answer.check, choices);
  const sent = new Set(leaveOutSent ? (sentHomes(answer) || []).map((h) => h.id) : []);
  const ids = s.add.filter((id) => !sent.has(String(id)));
  const sentLeftOut = s.add.length - ids.length;
  return { ids, leftOut: s.leftOut.length + ((answer && answer.missing) || []).length + sentLeftOut, sentLeftOut };
}

/** What the check did NOT look at, as words for the message: homes with no postcode, and homes in a mailshot that has been sent. */
export function notLookedAt(answer) {
  const a = answer || {}; const c = (a.check && a.check.counts) || {}; const out = [];
  if (c.noPostcode) out.push(`${homes(c.noPostcode)} with no postcode ${plural(c.noPostcode, 'was', 'were')} not compared with anything`);
  // an answer that says WHICH homes are already sent is acted on by the box, and said in the message's own words
  // (sentWords): only an answer that does not (a function from before that) is said here as "not held back"
  const sent = sentHomes(a) !== null ? [] : (a.elsewhere || []).filter((m) => m.sent && m.count > 0);
  const names = sent.map((m) => `"${m.name}"`).join(', ');
  // The server says how many DIFFERENT homes that is (`sentElsewhere`): a home in two sent mailshots is one home.
  // Adding up each mailshot's count would count it twice. An answer without that number (an older function) is
  // said by mailshot, and never added up.
  if (sent.length && Number.isInteger(a.sentElsewhere) && a.sentElsewhere > 0) out.push(`${homes(a.sentElsewhere)} ${plural(a.sentElsewhere, 'is', 'are')} already in a mailshot that has been sent (${names}), which this check does not hold back`);
  else if (sent.length) out.push(`some are already in a mailshot that has been sent (${sent.map((m) => `${n(m.count)} in "${m.name}"`).join(', ')}${sent.length > 1 ? '; a home in two of them is counted in each' : ''}), which this check does not hold back`);
  return out;
}

/**
 * The homes of groups too large to work out: how many groups, how many of their homes go in, and how many a person
 * left out. Such a group is NOT compared within itself: every home of it goes in unless a person leaves it out.
 */
export function largeGroups(answer, choices) {
  const groups = (answer && answer.check && answer.check.unsearched) || [];
  const out = (h) => (choices || {})[h.id] === 'out';
  const all = groups.flatMap((u) => u.homes || []);
  return { groups: groups.length, added: all.filter((h) => !out(h)).length, leftOut: all.filter(out).length };
}

/** What the message says of homes already in a sent mailshot: left out, or added because the box was unticked. Empty when there are none, or the answer does not say which. */
export function sentWords(answer, choices, leaveOutSent) {
  const choice = sentChoice(answer, choices, leaveOutSent);
  if (!choice || !choice.count) return '';
  return leaveOutSent
    ? `${homes(choice.count)} already in a mailshot that has been sent ${plural(choice.count, 'was', 'were')} left out`
    : `the box for homes already in a sent mailshot was UNTICKED: ${homes(choice.count)} already in a mailshot that has been sent ${plural(choice.count, 'was', 'were')} added`;
}

/** What is added to the success message when the check was made. */
export function checkedWords(answer, choices, { leaveOutSent = false } = {}) {
  const c = (answer && answer.check && answer.check.counts) || {};
  // the doubles and do not contact, as before; homes already sent are said apart, in their own words
  const s = settled(answer, choices);
  const sentSaid = sentWords(answer, choices, leaveOutSent);
  const also = [...(sentSaid ? [sentSaid] : []), ...notLookedAt(answer)];
  const tail = also.length ? `; ${also.join('; ')}` : '';
  // a group too large to work out went in without its homes being compared with each other: the message must not say "no doubles" of them
  const big = largeGroups(answer, choices);
  const bigWords = big.groups && big.added ? `; ${homes(big.added)} in ${n(big.groups)} ${plural(big.groups, 'group', 'groups')} ${plural(big.added, 'was', 'were')} too alike to work out and ${plural(big.added, 'was', 'were')} added WITHOUT being compared with each other` : '';
  if (!c.listed && !s.leftOut) return `checked first: no doubles and no do-not-contact matches among the homes ${big.groups ? 'that could be compared' : 'compared'}${bigWords}${tail}`;
  // ⚠️ only those that were SENT to the add: a home a person released and the "already sent" box then held back was
  // not added, and is said among the homes left out for that, not here (the second reviewer's S2)
  const going = new Set(settled(answer, choices, { leaveOutSent }).ids.map(String));
  const released = ((answer && answer.check && answer.check.listed) || []).filter((l) => ((choices || {})[l.id] === 'add' || (choices || {})[l.id] === 'instead') && going.has(String(l.id))).length;
  return `checked first: ${n(s.leftOut)} left out${released ? `, ${n(released)} added by your choice` : ''}${bigWords}${tail}`;
}

/**
 * What the message says when homes were added WITHOUT the check: because it was switched off,
 * or because it could not be made. `kept` is how many homes an earlier check had found to
 * match do not contact, which stay out all the same. `sentKept`: how many an earlier check had found in a mailshot
 * that has been sent, left out because their box is ticked. `sentAdded`: how many such homes were ADDED because a
 * person unticked that box: the check had told, so the message must not say "could not be told" of them.
 */
export function uncheckedWords(why, kept, sentKept = 0, sentAdded = 0) {
  const reason = why === 'failed' ? 'the check could not be made' : 'the check was switched off';
  const stayed = kept ? `; ${homes(kept)} the check had already found to match do not contact ${plural(kept, 'was', 'were')} left out` : '';
  // homes already in a sent mailshot: only the check can tell. One it had already named stays out while its box is ticked.
  const sent = sentAdded
    ? `; the box for homes the check had found already in a mailshot that has been sent was UNTICKED: ${homes(sentAdded)} ${plural(sentAdded, 'was', 'were')} added, and any other such home could not be told`
    : sentKept
      ? `; ${homes(sentKept)} the check had already found in a mailshot that has been sent ${plural(sentKept, 'was', 'were')} left out, and any other such home could not be told`
      : '; homes already in a mailshot that has been sent could not be told and were not held back';
  return `NOT checked for doubles or do not contact: ${reason}${stayed}${sent} (the add itself still refuses a record that is itself marked do not contact)`;
}

/** What to say about the time an add takes, when it is long enough to matter; null otherwise. */
export function addTimeWords(count) {
  if (!(count >= 800)) return null;
  const seconds = Math.round(count / 100);
  const words = `Adding ${homes(count)} takes about ${n(seconds)} seconds (ten seconds per thousand, measured on a test database; a scrape's larger records may take longer).`;
  return count > 2500 ? `${words} The add stops at one minute: above about 2,500 homes, add in two parts.` : words;
}
